import React, { useState, useEffect } from 'react';
import { ShieldCheck, Upload, AlertTriangle, CheckCircle2, Loader2, FileCheck } from 'lucide-react';

export default function MitarbeiterVerification() {
  const [status, setStatus] = useState('loading');
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => { fetchStatus(); }, []);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('tester_token');
      const response = await fetch('/api/employee/status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStatus(data.verification_status);
      }
    } catch (err) { console.error(err); }
  };

  const handleUpload = async () => {
    if (!frontFile || !backFile) return alert('Bitte lade sowohl die Vorderseite als auch die Rückseite deines Ausweises hoch.');
    setIsUploading(true);
    
    const formData = new FormData();
    formData.append('file_front', frontFile);
    formData.append('file_back', backFile);

    try {
      const token = localStorage.getItem('tester_token');
      const response = await fetch('/api/employee/upload-id', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        setStatus('pending');
      } else {
        alert('Fehler beim Upload der Dokumente. Möglicherweise ist die Datei zu groß.');
      }
    } catch (err) {
      console.error(err);
      alert('Netzwerkfehler. Bitte prüfe deine Internetverbindung.');
    } finally {
      setIsUploading(false);
    }
  };

  if (status === 'loading') {
    return <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-accent" /></div>;
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 max-w-3xl">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-primary">Identitätsprüfung</h1>
        <p className="text-muted-foreground">Für die Durchführung von Banken- und Fintech-Tests ist eine Verifizierung zwingend erforderlich.</p>
      </div>

      {status === 'verified' && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center flex flex-col items-center">
          <CheckCircle2 className="w-16 h-16 text-green-600 mb-4" />
          <h2 className="text-xl font-bold text-green-800 mb-2">Sie sind verifiziert!</h2>
          <p className="text-green-700">Ihre Identität wurde erfolgreich von unserem Team bestätigt. Sie können nun alle Premium-Aufträge annehmen.</p>
        </div>
      )}

      {status === 'pending' && (
        <div className="bg-white border border-border rounded-xl p-8 text-center flex flex-col items-center shadow-sm">
          <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
            <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
          </div>
          <h2 className="text-xl font-bold text-primary mb-2">Dokumente werden geprüft</h2>
          <p className="text-muted-foreground max-w-md">Ihre Ausweisdokumente (Vorder- und Rückseite) wurden erfolgreich übermittelt und werden aktuell von einem Admin überprüft.</p>
        </div>
      )}

      {(status === 'none' || status === 'rejected') && (
        <div className="bg-white border border-border rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 bg-amber-50 border-b border-amber-100 flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-amber-800">
                {status === 'rejected' ? 'Dokument abgelehnt - Bitte erneut versuchen' : 'Ausweis-Upload (2-Schritte)'}
              </h3>
              <p className="text-sm text-amber-700 mt-1">Bitte laden Sie ein gut lesbares Foto der Vorder- und Rückseite Ihres Personalausweises oder Reisepasses hoch.</p>
            </div>
          </div>
          
          <div className="p-8 space-y-6">
            <div className="border border-border rounded-xl p-6 bg-slate-50/50">
              <h4 className="font-bold text-primary mb-2 flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-accent" /> Schritt 1: Ausweis Vorderseite
              </h4>
              <label className="border-2 border-dashed border-border rounded-lg p-6 flex flex-col items-center justify-center text-center hover:bg-white transition-colors cursor-pointer bg-white">
                {/* HIER IST DER FIX FÜR APPLE GERÄTE */}
                <input type="file" className="hidden" accept="image/jpeg, image/png, application/pdf" onChange={(e) => setFrontFile(e.target.files[0])} />
                <Upload className="w-6 h-6 text-slate-400 mb-2" />
                <span className="text-sm font-medium text-primary">
                  {frontFile ? frontFile.name : 'Vorderseite auswählen (JPG, PNG, PDF)'}
                </span>
              </label>
            </div>

            <div className="border border-border rounded-xl p-6 bg-slate-50/50">
              <h4 className="font-bold text-primary mb-2 flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-accent" /> Schritt 2: Ausweis Rückseite
              </h4>
              <label className="border-2 border-dashed border-border rounded-lg p-6 flex flex-col items-center justify-center text-center hover:bg-white transition-colors cursor-pointer bg-white">
                {/* HIER IST DER FIX FÜR APPLE GERÄTE */}
                <input type="file" className="hidden" accept="image/jpeg, image/png, application/pdf" onChange={(e) => setBackFile(e.target.files[0])} />
                <Upload className="w-6 h-6 text-slate-400 mb-2" />
                <span className="text-sm font-medium text-primary">
                  {backFile ? backFile.name : 'Rückseite auswählen (JPG, PNG, PDF)'}
                </span>
              </label>
            </div>
            
            <button 
              onClick={handleUpload}
              disabled={!frontFile || !backFile || isUploading}
              className="w-full bg-primary text-white py-3.5 rounded-md font-medium hover:bg-secondary transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isUploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />} 
              {isUploading ? 'Wird hochgeladen...' : 'Beide Seiten zur Prüfung einreichen'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
